import airbridge from 'airbridge-web-sdk-loader';
import { EventOption } from 'airbridge-web-sdk-loader/build/airbridge_api';
import { LoginProvider, UserState } from '~/types';
import { AIRBRIDGE } from '~/constants/airbridge';
import { isEventLoggingValid } from './eventTracker';

const sendAirbridgeEvent = (
  email: string,
  category: string,
  option?: EventOption,
) => isEventLoggingValid(email) && airbridge.events.send(category, option);

const formatAgeData = (age: number) => {
  if (age < 10) return '10대 이하';
  if (age >= 50) return '50대 이상';
  return Math.floor(age / 10) * 10 + '대';
};

const signUp = (email: string, user: UserState, type: LoginProvider) => {
  const nowYear = new Date().getFullYear();
  // 02121999
  const userYear = Number(user.birth_date.slice(-4));
  airbridge.setUserId(user.name);
  airbridge.setUserEmail(user.email);
  airbridge.setUserAttributes({
    account_type: type,
    name: user.name,
    nickname: user.nickname,
    language: user.language,
    gender: user.gender,
    birth_date: user.birth_date,
    privacy_agree: user.privacy_agree,
    avatar_agree: user.avatar_agree,
    marketing_agree: user.marketing_agree,
  });
  sendAirbridgeEvent(email, AIRBRIDGE.EVENT.SIGN_UP, {
    action: user.gender ?? '-',
    label: formatAgeData(nowYear - userYear),
  });
};

const signIn = (email: string, user: UserState) => {
  const nowYear = new Date().getFullYear();
  // 1999-02-10
  const userYear = Number(user.birth_date.split('-')[0]);
  airbridge.setUserId(user.name);
  airbridge.setUserEmail(user.email);
  airbridge.setUserAttributes({
    name: user.name,
    nickname: user.nickname,
    language: user.language,
    gender: user.gender,
    birth_date: user.birth_date,
    privacy_agree: user.privacy_agree,
    avatar_agree: user.avatar_agree,
    marketing_agree: user.marketing_agree,
  });
  sendAirbridgeEvent(email, AIRBRIDGE.EVENT.SIGN_IN, {
    action: user.gender ?? '-',
    label: formatAgeData(nowYear - userYear),
  });
};

const signOut = (email: string) => {
  sendAirbridgeEvent(email, AIRBRIDGE.EVENT.SIGN_OUT);
  airbridge.clearUser();
};

const homeView = (email: string) => {
  sendAirbridgeEvent(email, AIRBRIDGE.EVENT.HOME_VIEW);
};

export type MessageType = 'chat' | 'groupchat' | 'game' | 'chat_game';

const sendMessage = (email: string, chatType: MessageType) => {
  // 'chat' | 'groupchat' | 'game' | 'chat_game'
  sendAirbridgeEvent(email, AIRBRIDGE.EVENT.SEND_MESSAGE, {
    action: chatType,
  });
};

export type DartPurchaseEvent = {
  product_id: string;
  product_name: string; // 다트 개수
  product_price: number;
  product_quantity: number;
  inapp_purchased: boolean; // true - 아직 인앱 결제만 지원
  currency: string;
  status: 'initiateCheckout' | 'orderCompleted' | 'clickBuyDart';
};

const purchaseDart = (
  email: string,
  {
    product_id,
    product_name,
    product_price,
    product_quantity,
    inapp_purchased,
    currency,
    status,
  }: DartPurchaseEvent,
) => {
  const getEventCategoryName = () => {
    switch (status) {
      case 'clickBuyDart':
        return AIRBRIDGE.EVENT.CLICK_BUY_DART;
      case 'initiateCheckout':
        return AIRBRIDGE.EVENT.INITIATE_CHECKOUT;
      case 'orderCompleted':
        return AIRBRIDGE.EVENT.ORDER_COMPLETED;
      default:
        return null;
    }
  };

  const airbridgeStatus = getEventCategoryName();
  if (!airbridgeStatus) return;
  sendAirbridgeEvent(email, airbridgeStatus, {
    action: product_id,
    label: product_name,
    value: product_price,
    semanticAttributes: {
      product_id: product_id,
      product_name: product_name,
      product_price: product_price,
      product_quantity: product_quantity,
      currency: currency,
      in_app_purchased: inapp_purchased,
    },
  });
};

export const airbridgeEvent = {
  signUp,
  signIn,
  signOut,
  homeView,
  sendMessage,
  purchaseDart,
};
